import { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useParams } from 'react-router-dom'
import SignPanel from '../../components/SignPanel'
import StatusBadge from '../../components/StatusBadge'
import {
  deleteSignRequest, downloadDocumentOriginal, downloadSignedArtifact, fetchDocumentPdfUrl,
  fetchDocumentText, getDocument, inviteToSign, selfSignComplete, selfSignPrepare,
  type DocumentDetail, type SignatureRequestSummary,
} from '../../lib/portal'

type Panel = 'none' | 'self' | 'invite'

function baseName(doc: DocumentDetail): string {
  if (doc.originalFileName) {
    const dot = doc.originalFileName.lastIndexOf('.')
    return dot > 0 ? doc.originalFileName.slice(0, dot) : doc.originalFileName
  }
  return doc.title || 'belge'
}

export default function DocumentDetailPage() {
  const { id = '' } = useParams()
  const [doc, setDoc]       = useState<DocumentDetail | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [text, setText]     = useState<string | null>(null)
  const [panel, setPanel]   = useState<Panel>('none')
  const [error, setError]   = useState<string | null>(null)
  const [done, setDone]     = useState(false)

  const load = useCallback(() => {
    getDocument(id).then(setDoc).catch(() => setError('Belge bulunamadı.'))
  }, [id])

  useEffect(() => { load() }, [load])

  // Load the preview only once the view mode is known.
  useEffect(() => {
    if (!doc) return
    let revoked: string | null = null
    if (doc.viewMode === 'pdf') {
      fetchDocumentPdfUrl(id).then(url => { revoked = url; setPdfUrl(url) }).catch(() => {})
    } else if (doc.viewMode === 'text') {
      fetchDocumentText(id).then(setText).catch(() => setText('(Belge görüntülenemedi.)'))
    }
    return () => { if (revoked) URL.revokeObjectURL(revoked) }
  }, [id, doc])

  if (error) return <section className="container-page py-12"><p className="text-red-700">{error}</p></section>
  if (!doc)  return <section className="container-page py-12"><p className="text-[var(--color-fg-muted)]">Yükleniyor…</p></section>

  return (
    <>
      <Helmet><title>{doc.title} — W.Sign</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <section className="container-page py-12">
        <Link to="/dashboard" className="text-sm text-[var(--color-accent)] hover:underline">← Panele dön</Link>
        <div className="mt-2 flex items-center gap-3">
          <h1 className="text-3xl font-semibold">{doc.title}</h1>
          <StatusBadge status={doc.status} />
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          {/* Preview, by view mode */}
          <div className="card p-0 overflow-hidden">
            {doc.viewMode === 'pdf' ? (
              pdfUrl
                ? <iframe title="Belge" src={pdfUrl} className="h-[70vh] w-full" />
                : <p className="p-4 text-sm text-[var(--color-fg-muted)]">Önizleme yükleniyor…</p>
            ) : doc.viewMode === 'text' ? (
              <pre className="h-[70vh] w-full overflow-auto whitespace-pre-wrap break-words p-4 text-sm font-mono">
                {text ?? 'Yükleniyor…'}
              </pre>
            ) : (
              <div className="flex h-[70vh] flex-col items-center justify-center gap-4 p-6 text-center">
                <p className="text-sm text-[var(--color-fg-muted)]">
                  Bu belge türü ({doc.contentType}) tarayıcıda görüntülenemiyor.
                </p>
                <button
                  type="button" className="btn-primary"
                  onClick={() => downloadDocumentOriginal(id, doc.originalFileName ?? `${baseName(doc)}`)}
                >
                  Belgeyi indir
                </button>
              </div>
            )}
          </div>

          {/* Actions + history */}
          <div className="space-y-6">
            {done && (
              <SignedResultCard
                doc={doc}
                documentId={id}
                downloadBase={baseName(doc)}
              />
            )}

            <div className="card">
              <h2 className="font-semibold">İmza</h2>
              <div className="mt-3 flex flex-wrap gap-3">
                <button onClick={() => setPanel(panel === 'self' ? 'none' : 'self')} type="button" className="btn-primary">
                  Kendim imzala
                </button>
                <button onClick={() => setPanel(panel === 'invite' ? 'none' : 'invite')} type="button" className="btn-ghost">
                  E-posta ile imzalat
                </button>
              </div>

              {panel === 'self' && (
                <div className="mt-5 border-t border-[var(--color-border)] pt-5">
                  <SignPanel
                    isPdf={doc.viewMode === 'pdf'}
                    prepare={(cert, timestamp, longTerm) => selfSignPrepare(id, cert, timestamp, longTerm)}
                    complete={(sid, raw) => selfSignComplete(id, sid, raw)}
                    onSuccess={() => { setDone(true); setPanel('none'); load() }}
                  />
                </div>
              )}

              {panel === 'invite' && (
                <div className="mt-5 border-t border-[var(--color-border)] pt-5">
                  <InvitePanel documentId={id} onInvited={() => { setPanel('none'); load() }} />
                </div>
              )}
            </div>

            <div className="card">
              <h2 className="font-semibold">İmza geçmişi</h2>
              {doc.signatureRequests.length === 0 ? (
                <p className="mt-2 text-sm text-[var(--color-fg-muted)]">Henüz imza talebi yok.</p>
              ) : (
                <ul className="mt-3 space-y-3">
                  {doc.signatureRequests.map(r => (
                    <RequestRow
                      key={r.id} req={r} documentId={id} downloadBase={baseName(doc)}
                      isPdf={doc.viewMode === 'pdf'}
                      onChanged={load}
                    />
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

/**
 * Success card shown right after a signature completes: puts the signed file's download in front of
 * the user instead of leaving it buried in the "İmza geçmişi" list further down the column.
 * <p>
 * Signing ends with "imzalı dosyayı indir" — that was previously two scroll-and-hunt steps away, and
 * the only immediate feedback was the words "İşlem tamamlandı." A demo viewer who does not find the
 * artifact concludes the product did not produce one.
 * </p>
 * The artifact appears once the reload that follows the signature lands, so until then the card shows
 * a short waiting line rather than a dead button.
 */
function SignedResultCard({
  doc, documentId, downloadBase,
}: {
  doc: DocumentDetail; documentId: string; downloadBase: string
}) {
  const [error, setError] = useState<string | null>(null)
  const isPdf = doc.viewMode === 'pdf'

  // The signature just made is the most recently completed one that has an artifact.
  const latest = doc.signatureRequests
    .filter(r => r.hasSignedArtifact)
    .sort((a, b) => (b.completedAt ?? b.createdAt).localeCompare(a.completedAt ?? a.createdAt))[0]

  const download = async (format: 'imz' | 'p7s', fileName: string) => {
    setError(null)
    try {
      await downloadSignedArtifact(documentId, format, fileName, latest?.id)
    } catch {
      setError('İmzalı belge indirilemedi.')
    }
  }

  return (
    <div className="card border-emerald-200 bg-emerald-50">
      <p className="font-semibold text-emerald-900">İmza tamamlandı</p>

      {latest ? (
        <>
          <p className="mt-1 text-sm text-emerald-800">
            {isPdf
              ? 'İmza PDF’in içine gömüldü; dosyayı olduğu gibi açabilir ve doğrulatabilirsiniz.'
              : 'İmzalı dosyayı indirin. Ayrık (.p7s) imzayı doğrulatmak için orijinal belge de gerekir.'}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {isPdf ? (
              <button
                type="button" className="btn-primary"
                onClick={() => download('imz', `${downloadBase}-imzali.pdf`)}
              >
                İmzalı PDF’i indir
              </button>
            ) : (
              <>
                <button
                  type="button" className="btn-primary"
                  onClick={() => download('imz', `${downloadBase}.imz`)}
                >
                  İmzalı dosyayı indir (.imz)
                </button>
                <button
                  type="button" className="btn-ghost"
                  onClick={() => download('p7s', `${downloadBase}.p7s`)}
                >
                  .p7s (ayrık)
                </button>
              </>
            )}
            <Link to="/verify" className="btn-ghost">Doğrula</Link>
          </div>
        </>
      ) : (
        <p className="mt-1 text-sm text-emerald-800">İmzalı belge hazırlanıyor…</p>
      )}

      {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
    </div>
  )
}

function RequestRow({
  req, documentId, downloadBase, isPdf, onChanged,
}: {
  req: SignatureRequestSummary; documentId: string; downloadBase: string; isPdf: boolean; onChanged: () => void
}) {
  const [busy, setBusy]   = useState(false)
  const [error, setError] = useState<string | null>(null)

  const remove = async () => {
    if (!window.confirm('Bu imza talebini silmek istediğinize emin misiniz?')) return
    setBusy(true); setError(null)
    try {
      await deleteSignRequest(documentId, req.id)
      onChanged()
    } catch {
      setError('Silinemedi.')
      setBusy(false)
    }
  }

  // The download filename is decided here: a PAdES (PDF) signature is a signed PDF, so it must save
  // as .pdf — not the CAdES .imz/.p7s containers. The server returns the PDF regardless of `format`
  // for PAdES docs, so any format hint works; we use "imz".
  const download = async (format: 'imz' | 'p7s', fileName: string) => {
    setError(null)
    try {
      await downloadSignedArtifact(documentId, format, fileName, req.id)
    } catch {
      setError('İmzalı belge indirilemedi.')
    }
  }

  return (
    <li className="rounded border border-[var(--color-border)] p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">
          {req.kind === 'EmailInvite' ? `Davet · ${req.recipientEmail}` : 'Kendi imzası'}
        </span>
        <StatusBadge status={req.status} />
      </div>

      {req.kind === 'EmailInvite' && req.publicToken && req.status !== 'Signed' && (
        <p className="mt-1 break-all text-xs font-mono text-[var(--color-fg-dim)]">
          {`${window.location.origin}/s/${req.publicToken}`}
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {req.hasSignedArtifact && (
          isPdf ? (
            <button
              type="button" className="btn-ghost px-2 py-1 text-xs"
              onClick={() => download('imz', `${downloadBase}-imzali.pdf`)}
            >
              İmzalı PDF indir
            </button>
          ) : (
            <>
              <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => download('imz', `${downloadBase}.imz`)}>
                İmzalı indir (.imz)
              </button>
              <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => download('p7s', `${downloadBase}.p7s`)}>
                .p7s (ayrık)
              </button>
            </>
          )
        )}
        <button
          type="button" disabled={busy}
          className="rounded border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
          onClick={remove}
        >
          {busy ? 'Siliniyor…' : 'Sil'}
        </button>
        {error && <span className="text-xs text-red-700">{error}</span>}
      </div>
    </li>
  )
}

function InvitePanel({ documentId, onInvited }: { documentId: string; onInvited: () => void }) {
  const [email, setEmail] = useState('')
  const [busy, setBusy]   = useState(false)
  const [link, setLink]   = useState<string | null>(null)
  const [emailOk, setEmailOk] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (!email.includes('@')) { setError('Geçerli bir e-posta girin.'); return }
    setBusy(true); setError(null)
    try {
      const res = await inviteToSign(documentId, email)
      setLink(`${window.location.origin}/s/${res.publicToken}`)
      setEmailOk(res.emailDelivered !== false)
      onInvited()
    } catch {
      setError('Davet gönderilemedi.')
    } finally {
      setBusy(false)
    }
  }

  if (link) {
    return emailOk ? (
      <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm">
        <p className="text-emerald-900">Davet gönderildi. İmza bağlantısı:</p>
        <p className="mt-1 break-all font-mono text-xs text-emerald-800">{link}</p>
      </div>
    ) : (
      <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm">
        <p className="text-amber-900">
          İmza talebi oluşturuldu ancak e-posta gönderilemedi. Aşağıdaki bağlantıyı alıcıyla elle paylaşın:
        </p>
        <p className="mt-1 break-all font-mono text-xs text-amber-800">{link}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
      <label className="flex-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">Alıcı e-postası</span>
        <input
          type="email" value={email} placeholder="imzalayan@firma.com"
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void submit() }}
          className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none"
        />
      </label>
      <button onClick={submit} disabled={busy} type="button" className="btn-primary">
        {busy ? 'Gönderiliyor…' : 'Davet gönder'}
      </button>
      {error && <span className="text-sm text-red-700">{error}</span>}
    </div>
  )
}
